import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/Badges";
import { Loader2, PauseCircle, Play, Ban, ArrowRight, Filter, Target } from "lucide-react";

const FILTERS = [
  ["deployed", "Live"], ["all", "All"], ["pending", "Pending"],
  ["paused", "Paused"], ["completed", "Done"], ["cancelled", "Cancelled"],
];

function ProgressBar({ progress }) {
  const pct = progress?.pct || 0;
  const failed = progress?.failed || 0;
  const total = progress?.total || 0;
  return (
    <div>
      <div className="h-2 bg-[#1a1a1c] relative overflow-hidden">
        <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }}/>
        {failed > 0 && (
          <div className="h-full bg-[#FF3B30] absolute top-0" style={{ left: `${pct}%`, width: `${(failed/total)*100}%` }}/>
        )}
      </div>
      <div className="flex items-center justify-between mt-1 font-mono text-[10px] text-zinc-500">
        <span>{progress?.done || 0} done · {failed} failed{progress?.cancelled ? ` · ${progress.cancelled} cancelled` : ""}</span>
        <span>{pct.toFixed(0)}% / {total}</span>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("deployed");

  const q = useQuery({
    queryKey: ["campaigns", filter],
    queryFn: () => api.get(`/campaigns${filter && filter !== "all" ? `?status=${filter}` : ""}`).then(r => r.data),
    refetchInterval: 10000,
  });

  const doAction = useMutation({
    mutationFn: ({ id, action }) => api.post(`/campaigns/${id}/${action}`),
    onSuccess: () => { qc.invalidateQueries({queryKey:["campaigns"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <Layout title="Campaign Dashboard." subtitle="All live · paused · completed campaigns">
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500"/>
        {FILTERS.map(([k, label]) => (
          <button key={k}
            onClick={() => setFilter(k)}
            data-testid={`campaign-filter-${k}`}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest border ${
              filter === k ? "bg-white text-black border-white" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
          >{label}</button>
        ))}
      </div>

      {q.isLoading && <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>}

      <div className="grid md:grid-cols-2 gap-4 min-w-0">
        {q.data?.length === 0 && (
          <div className="col-span-2 border border-dashed border-white/15 p-12 text-center bg-dots">
            <Target className="w-8 h-8 text-zinc-700 mx-auto mb-4"/>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no campaigns</div>
            <div className="text-zinc-400 text-sm">Head to <Link to="/admin/orchestrate" className="text-[#FF3B30] hover:underline">Orchestrate</Link> to create one.</div>
          </div>
        )}
        {q.data?.map((c) => {
          const status = c.admin_approval_status || "?";
          const isPaused = status === "Paused";
          const isCancelled = status === "Cancelled";
          const isCompleted = status === "Completed" || (c.progress?.done > 0 && c.progress?.done === c.progress?.total);
          const isLive = !!c.deployed_at && !isPaused && !isCancelled;
          return (
            <div key={c.id} data-testid={`campaign-card-${c.id}`} className="border border-white/10 p-5 hover:border-white/25 flex flex-col min-w-0 overflow-hidden">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusBadge status={status}/>
                    {c.source === "manual" && <span className="text-[9px] font-mono uppercase tracking-widest border border-[#007AFF]/40 text-[#007AFF] px-1.5 py-0.5">manual</span>}
                    {c.product && <span className="text-[9px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-400 px-1.5 py-0.5 truncate max-w-[140px]">{c.product.name}</span>}
                  </div>
                  <div className="text-[14px] font-medium text-zinc-100 line-clamp-2">{c.objective}</div>
                  {c.thread && (
                    <div className="mt-1 text-[11px] font-mono text-zinc-500 truncate">
                      <span className="text-[#FF3B30]">{c.thread.subreddit}</span> · {c.thread.title?.slice(0, 60)}
                    </div>
                  )}
                </div>
              </div>

              <div className="my-3">
                <ProgressBar progress={c.progress}/>
              </div>

              <div className="mt-auto pt-3 border-t border-white/5 flex items-center gap-2">
                <Link to={`/admin/threads/${c.thread_id}`}
                      data-testid={`open-campaign-${c.id}`}
                      className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1">
                  Open <ArrowRight className="w-3 h-3"/>
                </Link>
                <div className="ml-auto flex items-center gap-1">
                  {isLive && (
                    <button
                      onClick={() => { if (window.confirm("Pause this campaign? Remaining tasks will be hidden.")) doAction.mutate({id: c.id, action: "pause"}); }}
                      disabled={doAction.isPending}
                      data-testid={`pause-${c.id}`}
                      title="Pause"
                      className="p-1.5 border border-white/10 hover:border-amber-400 text-zinc-400 hover:text-amber-400"
                    >
                      <PauseCircle className="w-3.5 h-3.5"/>
                    </button>
                  )}
                  {isPaused && (
                    <button
                      onClick={() => doAction.mutate({id: c.id, action: "resume"})}
                      disabled={doAction.isPending}
                      data-testid={`resume-${c.id}`}
                      title="Resume"
                      className="p-1.5 border border-white/10 hover:border-emerald-400 text-zinc-400 hover:text-emerald-400"
                    >
                      <Play className="w-3.5 h-3.5"/>
                    </button>
                  )}
                  {(isLive || isPaused) && !isCompleted && (
                    <button
                      onClick={() => { if (window.confirm("Cancel this campaign? Pending tasks will be cancelled.")) doAction.mutate({id: c.id, action: "cancel"}); }}
                      disabled={doAction.isPending}
                      data-testid={`cancel-${c.id}`}
                      title="Cancel"
                      className="p-1.5 border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]"
                    >
                      <Ban className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
